import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { crumbsFor } from '../../navigation/modules';
import api from '../../services/api';
import { ghost, muted, card, rowBorder, errorText } from '../time/styles';
import { Message } from '../time/ui';
import TaskPanel from './TaskPanel';
import { TaskStatus } from './taskUi';
import { dueText } from './docUtils';

// Everyone → My tasks: onboarding and exit tasks this person owns (their own
// joining paperwork, or work for someone else's joining or leaving).

const MyTasks = () => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const preJoining = user.status === 'PRE_JOINING';
    const isHr = user.role === 'HR' || user.role === 'ADMIN';
    const [tasks, setTasks] = useState(null);
    const [toReview, setToReview] = useState([]);
    const [settlements, setSettlements] = useState({ approvals: [], toPay: [] });
    const [done, setDone] = useState(null);
    const [openId, setOpenId] = useState(null);
    const [message, setMessage] = useState(null);
    const say = useCallback((ok, text) => setMessage({ ok, text }), []);
    const load = useCallback(() => {
        api.get('/lifecycle/tasks/mine').then(r => { setTasks(r.data.tasks); setToReview(r.data.toReview || []); }).catch(e => say(false, errorText(e, 'Could not load your tasks')));
        // Someone who has not joined yet has no settlements to approve.
        if (!preJoining) api.get('/lifecycle/settlements/approvals').then(r => setSettlements({ approvals: r.data.approvals, toPay: r.data.toPay || [] })).catch(e => say(false, errorText(e, 'Could not load settlements waiting for you')));
        // Keep the finished list current once it is shown.
        setDone(prev => { if (prev !== null) api.get('/lifecycle/tasks/mine?done=1').then(r => setDone(r.data.tasks)).catch(() => {}); return prev; });
    }, [say, preJoining]);
    useEffect(() => { load(); }, [load]);
    const showDone = () => api.get('/lifecycle/tasks/mine?done=1').then(r => setDone(r.data.tasks)).catch(e => say(false, errorText(e, 'Could not load finished tasks')));

    const mine = tasks?.filter(t => t.case.isMine) || [];
    const others = tasks?.filter(t => !t.case.isMine) || [];

    const row = (t) => (
        <li key={t.id} style={{ borderTop: rowBorder }}>
            <button onClick={() => setOpenId(openId === t.id ? null : t.id)} aria-expanded={openId === t.id}
                style={{ width: '100%', background: 'none', border: 0, padding: '12px 0', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', textAlign: 'left', color: 'var(--text-main)' }}>
                <span style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                    {openId === t.id ? <ChevronDown size={16} style={{ marginTop: 3 }} /> : <ChevronRight size={16} style={{ marginTop: 3 }} />}
                    <span>
                        <span style={{ fontWeight: 500 }}>{t.title}</span>
                        <span style={{ display: 'block', ...muted }}>
                            {t.case.isMine ? '' : `${t.case.user.name}'s ${t.case.kind === 'ONBOARDING' ? 'onboarding' : 'exit'} · `}{t.typeLabel}{!t.required ? ' · optional' : ''}
                        </span>
                    </span>
                </span>
                <span style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                    <span style={{ fontSize: 13, color: t.overdue ? '#ef4444' : 'var(--text-muted)' }}>{dueText(t)}</span>
                    <TaskStatus status={t.status} />
                </span>
            </button>
            {openId === t.id && <div style={{ paddingLeft: 24 }}><TaskPanel taskId={t.id} hr={isHr} say={say} onChanged={load} /></div>}
        </li>
    );

    return (
        <div className="container" style={{ padding: '2rem 1rem', maxWidth: 900 }}>
            <PageHeader crumbs={crumbsFor('tasks')} />
            <h1 style={{ marginBottom: 4 }}>My tasks</h1>
            <p style={{ ...muted, fontSize: 15, marginTop: 0 }}>Things to do for joining and leaving: your own, and ones you help with.</p>
            <Message message={message} />
            {[['approvals', 'Settlements waiting for your approval', '#d97706'], ['toPay', 'Settlements ready to pay', '#10b981']].map(([k, title, color]) => settlements[k].length > 0 && (
                <section key={k} className="card" style={{ ...card, borderLeft: `4px solid ${color}` }} aria-label={title}>
                    <h2 style={{ fontSize: 17, margin: '0 0 4px' }}>{title} ({settlements[k].length})</h2>
                    <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                        {settlements[k].map(a => (
                            <li key={a.id} data-testid={`settlement-${k}-${a.user.name}`} style={{ borderTop: rowBorder, padding: '10px 0', display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'center', color: 'var(--text-main)' }}>
                                <span><strong>{a.user.name}</strong> <span style={muted}>· {a.direction === 'RECOVERABLE' ? 'to recover' : 'to pay'} {new Intl.NumberFormat('en-IN', { style: 'currency', currency: a.currency }).format(Math.abs(a.netPayable))}{k === 'toPay' ? ` · due ${a.dueDate}` : ''}</span></span>
                                <Link to={`/settlements/${a.id}`} style={ghost}>Open</Link>
                            </li>
                        ))}
                    </ul>
                </section>
            ))}
            {toReview.length > 0 && (
                <section className="card" style={{ ...card, borderLeft: '4px solid #8b5cf6' }}>
                    <h2 style={{ fontSize: 17, margin: '0 0 4px' }}>To review ({toReview.length})</h2>
                    <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>{toReview.map(row)}</ul>
                </section>
            )}
            {!tasks ? <p style={muted}>Loading…</p> : tasks.length === 0 && settlements.approvals.length === 0 && settlements.toPay.length === 0 && toReview.length === 0 ? (
                <section className="card" style={card}><p style={{ margin: 0, color: 'var(--text-main)' }}>Nothing to do right now.</p></section>
            ) : (
                <>
                    {mine.length > 0 && (
                        <section className="card" style={card}>
                            <h2 style={{ fontSize: 17, margin: '0 0 4px' }}>For your {mine[0].case.kind === 'ONBOARDING' ? 'joining' : 'exit'}</h2>
                            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>{mine.map(row)}</ul>
                        </section>
                    )}
                    {others.length > 0 && (
                        <section className="card" style={card}>
                            <h2 style={{ fontSize: 17, margin: '0 0 4px' }}>For colleagues</h2>
                            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>{others.map(row)}</ul>
                        </section>
                    )}
                </>
            )}
            {done === null ? <button style={ghost} onClick={showDone}>Show finished tasks</button> : (
                <section className="card" style={card}>
                    <h2 style={{ fontSize: 17, margin: '0 0 4px' }}>Finished</h2>
                    {done.length === 0 ? <p style={muted}>None yet.</p> : (
                        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                            {done.map(t => (
                                <li key={t.id} style={{ borderTop: rowBorder, padding: '8px 0', display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                                    <span style={{ color: 'var(--text-main)' }}>{t.title} <span style={muted}>{t.case.isMine ? '' : `· ${t.case.user.name}`}</span></span>
                                    <TaskStatus status={t.status} />
                                </li>
                            ))}
                        </ul>
                    )}
                </section>
            )}
        </div>
    );
};

export default MyTasks;
